/**
 * PredictiveEngine – rule-based predictive insights surfaced on every
 * screen (frontend-only, no API calls). Uses the same UIContext extracted
 * for the recommendation engine, so it stays in lock-step with whatever
 * the user is currently looking at.
 *
 * Outputs are intentionally human-friendly: each prediction has a
 * `message`, a `reason` (why we predicted this), and a
 * `recommendation` (what the user should do about it).
 *
 * Pure function — same context produces the same predictions.
 */

import type { UIContext } from './UIContextExtractor';
import { findKpi, kpiValue } from './UIContextExtractor';

export type PredictionLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export type PredictionType =
  | 'RISK'           // negative outcome likely
  | 'SLA_RISK'       // workflow may breach SLA
  | 'INSIGHT'        // observation, not necessarily bad
  | 'OPPORTUNITY';   // positive action available

export interface Prediction {
  id: string;
  type: PredictionType;
  level: PredictionLevel;
  message: string;
  reason: string;
  recommendation: string;
  /** Optional CSS selector to scroll/highlight when user clicks the card. */
  target?: string;
}

const LEVEL_RANK: Record<PredictionLevel, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

export function sortByLevel(list: Prediction[]): Prediction[] {
  return [...list].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function numericAttr(selector: string, attr: string): number | undefined {
  const el = document.querySelector(selector);
  if (!el) return undefined;
  const raw = el.getAttribute(attr);
  if (raw === null || raw === '') return undefined;
  const n = Number(String(raw).replace(/[, ]/g, ''));
  return Number.isFinite(n) ? n : undefined;
}

function moneyToNumber(v: string | number | undefined): number | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v === 'number') return v;
  const cleaned = String(v).replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

// ──────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────

export function generatePredictions(ctx: UIContext): Prediction[] {
  const out: Prediction[] = [];

  // ─── Risk: high-value case with missing documents ───────────────────
  const approvedAmt = moneyToNumber(ctx.entity?.approvedAmount);
  const missingDocs =
    numericAttr('[data-missing-documents]', 'data-missing-documents') ??
    (numericAttr('[data-document-count]', 'data-document-count') === 0 ? 1 : undefined);

  if (
    (ctx.pageType === 'Patient360' || ctx.pageType === 'CaseDetail') &&
    approvedAmt !== undefined &&
    approvedAmt >= 100000 &&
    missingDocs !== undefined &&
    missingDocs > 0
  ) {
    out.push({
      id: 'risk-rejection-high-value',
      type: 'RISK',
      level: 'HIGH',
      message: 'This case has a high probability of rejection',
      reason: `Approved amount is ${approvedAmt.toLocaleString()} with ${missingDocs} missing document${missingDocs === 1 ? '' : 's'}.`,
      recommendation: 'Upload the required documents before sending for approval.',
      target: '[data-section-name="documents"]',
    });
  }

  // ─── SLA risk based on data attributes ─────────────────────────────
  const slaElapsed = numericAttr('[data-sla-elapsed-pct]', 'data-sla-elapsed-pct');
  const slaBreached = ctx.workflow.slaBreached;

  if (slaBreached > 0) {
    out.push({
      id: 'sla-already-breached',
      type: 'SLA_RISK',
      level: 'CRITICAL',
      message: `${slaBreached} task${slaBreached === 1 ? '' : 's'} already breached SLA`,
      reason: 'Service-level deadline has passed.',
      recommendation: 'Escalate or complete these tasks immediately.',
    });
  } else if (slaElapsed !== undefined && slaElapsed >= 80) {
    out.push({
      id: 'sla-risk-imminent',
      type: 'SLA_RISK',
      level: slaElapsed >= 95 ? 'HIGH' : 'MEDIUM',
      message: 'This task is likely to breach SLA soon',
      reason: `${slaElapsed}% of the SLA window has elapsed.`,
      recommendation: 'Complete the approval before the deadline.',
    });
  }

  // ─── Insight: low utilization ──────────────────────────────────────
  const utilization = kpiValue(ctx, 'utiliz');
  if (utilization > 0 && utilization < 40) {
    out.push({
      id: 'low-utilization',
      type: 'INSIGHT',
      level: 'LOW',
      message: 'Utilization is lower than expected',
      reason: `Current utilization is ${utilization}%.`,
      recommendation: 'Review patient engagement and follow-up cadence.',
    });
  }

  // ─── Insight: high pending tasks ───────────────────────────────────
  if (ctx.workflow.pendingTasks >= 10) {
    out.push({
      id: 'high-pending-backlog',
      type: 'INSIGHT',
      level: ctx.workflow.pendingTasks >= 20 ? 'HIGH' : 'MEDIUM',
      message: 'Pending task backlog is growing',
      reason: `${ctx.workflow.pendingTasks} tasks awaiting action.`,
      recommendation: 'Triage by priority or escalate to a manager.',
    });
  }

  // ─── Opportunity: patient with no cases yet ───────────────────────
  if (ctx.pageType === 'Patient360' && ctx.entity?.cases === 0) {
    out.push({
      id: 'opportunity-first-case',
      type: 'OPPORTUNITY',
      level: 'MEDIUM',
      message: 'Patient is enrolled but has no assistance case yet',
      reason: 'Zero active cases on file.',
      recommendation: 'Create the first assistance case to begin treatment support.',
      target: '[data-demo="create-case-btn"]',
    });
  }

  // ─── Risk: rejection ratio (dashboard) ─────────────────────────────
  const rejectionRate = kpiValue(ctx, 'reject');
  if (rejectionRate >= 25) {
    out.push({
      id: 'high-rejection-rate',
      type: 'RISK',
      level: rejectionRate >= 50 ? 'HIGH' : 'MEDIUM',
      message: 'Rejection rate is unusually high',
      reason: `${rejectionRate}% of recent cases rejected.`,
      recommendation: 'Review rejection reasons and tighten pre-approval checks.',
    });
  }

  // ─── Insight: KPI trend down ───────────────────────────────────────
  const enrollment = findKpi(ctx, 'enrol') ?? findKpi(ctx, 'new');
  if (enrollment?.trend === 'down') {
    out.push({
      id: 'enrollment-trend-down',
      type: 'INSIGHT',
      level: 'MEDIUM',
      message: 'Enrollment is trending downward',
      reason: `${enrollment.label} is below the recent baseline.`,
      recommendation: 'Coordinate with field teams or HCPs to drive new referrals.',
    });
  }

  return sortByLevel(out);
}

export function levelLabel(l: PredictionLevel): string {
  return l.charAt(0) + l.slice(1).toLowerCase();
}
